import { createContext, PropsWithChildren, useEffect, useState } from 'react';
import { HqoClientSdk } from '@hqo/hqo-miniapp-client-sdk';

import { useContextValue } from 'app/shared/hooks/use-context-value';
import { useUiMetadataQuery } from 'app/data/queries/use-ui-metadata-query';

const MiniappSdkContext = createContext<HqoClientSdk | null>(null);

/**
 * Instantiates the HqO miniapp SDK exactly once, after `app_uuid` is known.
 *
 * The SDK constructor posts an `init` message to the host frame; the host
 * answers with `initResponse` (carrying user/building/theme). We can't
 * recreate the SDK on every render — that would lose the registered event
 * handlers and break the init handshake. So we lazily create it the first
 * time `app_uuid` becomes available and never replace it.
 */
export const MiniappSdkProvider = ({ children }: PropsWithChildren): JSX.Element => {
  const { uiMetadata } = useUiMetadataQuery();
  const appUuid = uiMetadata?.app_uuid;
  const [sdk, setSdk] = useState<HqoClientSdk | null>(null);

  useEffect(() => {
    if (sdk || !appUuid) return;
    setSdk(
      // `develop: true` activates the SDK's built-in mocked data path (a
      // "Test User" fixture etc.). It would short-circuit the real fetches
      // we rely on in Cypress/preview environments — keep it off and use
      // MSW or the host frame for mocks instead.
      new HqoClientSdk({
        develop: false,
        app_uuid: appUuid,
        init_timeout: undefined,
      }),
    );
  }, [appUuid, sdk]);

  return <MiniappSdkContext.Provider value={sdk}>{children}</MiniappSdkContext.Provider>;
};

export const useMiniappSdk = (): HqoClientSdk | null => useContextValue(MiniappSdkContext);

export { MiniappSdkContext };
